import React, { useEffect } from "react";
import redDeleteIcon from "../../../assets/icons/red-delete-icon.svg";
import "./email-letter-mass-actions.scss";
import MoveTemplateToFolderPopup from "./move-to-folder-popup";
import { ConfirmationPopup } from "../../../common/components";
import { EmailLetterMassActionsProps } from "../email-letter-types";
import {
  deleteBulkTemplates,
  exportTemplates,
} from "../../../api/email-letter-templates";
import { useResourcePermissions } from "../../../common/hooks/resource-permissions";
import MODULES from "../../../constants/modules";
import importDownArrowIcon from "../../../assets/icons/import-down-arrow.svg";

const EmailLetterMassActions: React.FC<EmailLetterMassActionsProps> = ({
  selectedEmailTemplatesMap,
  setSelectedEmailTemplatesMap,
  folderOptions = [],
  templateType = "Email",
  fetchFoldersList = () => {},
  moveTemplateAPI,
  setAlertMessage,
  setLoading,
  fetchTemplatesList,
}) => {
  const [isMoveToFolderOpen, setIsMoveToFolderOpen] = React.useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
  const [isExportOpen, setIsExportOpen] = React.useState(false);
  const moduleName =
    templateType === "Email"
      ? MODULES.EMAIL_TEMPLATES
      : MODULES.LETTER_TEMPLATES;
  const { canRead, canWrite, canDelete } = useResourcePermissions(moduleName);
  const { canExport } = useResourcePermissions(MODULES.EXPORT);

  const handleExport = () => {
    // Logic to export selected email templates
    exportTemplatesAPI();
  };

  const handleMoveToFolder = (folder: { id: string; folder_name: string }) => {
    const templatedIds = Object.values(selectedEmailTemplatesMap).flat();
    if (templatedIds.length === 0) {
      console.error("No templates selected for moving.");
      return;
    }

    moveTemplateAPI?.(
      {
        templateIds: templatedIds,
        folderId: folder.id,
        templateType: templateType,
        notInAFolder: false,
      },
      folder.folder_name
    );
    setSelectedEmailTemplatesMap({});
    setIsMoveToFolderOpen(false);
  };

  const deleteTemplatesApi = async () => {
    const templatedIds = Object.values(selectedEmailTemplatesMap).flat();
    if (templatedIds.length === 0) {
      console.error("No templates selected for deletion.");
      return;
    }
    try {
      setLoading?.(true);
      const params = {
        templateType: templateType,
      };
      const response = await deleteBulkTemplates(
        {
          templateIds: templatedIds,
        },
        params
      );
      if (response?.data?.success === true) {
        setAlertMessage({
          open: true,
          message: `${templatedIds?.length} ${templatedIds?.length === 1 ? `${templateType} template` : `${templateType} templates`} archived successfully.`,
          type: "success",
        });

        setSelectedEmailTemplatesMap({});
        fetchTemplatesList?.();
      }
    } catch (error: any) {
      console.error("Error deleting templates:", error);
      setAlertMessage({
        open: true,
        message:
          error?.response?.data?.message || "Failed to delete templates.",
        type: "error",
      });
    } finally {
      setLoading?.(false);
      setIsDeleteOpen(false);
    }
  };

  const handleDelete = () => {
    deleteTemplatesApi();
  };

  const exportTemplatesAPI = async () => {
    const templatedIds = Object.values(selectedEmailTemplatesMap).flat();
    if (templatedIds.length === 0) {
      console.error("No templates selected for export.");
      return;
    }
    try {
      setLoading?.(true);
      // Logic to export templates, e.g., making an API call
      const response = await exportTemplates(
        {
          templateIds: templatedIds,
          format: "xml",
        },
        {
          templateType: templateType,
        }
      );
      if (response) {
        // Create a Blob from the response data
        const blob = new Blob([response.data], { type: "application/xml" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${templateType}TemplatesExport-${new Date().toISOString()}.xml`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        // setAlertText?.(`${templatedIds?.length} ${templatedIds?.length === 1 ? "template" : "templates"} exported successfully.`);
        // setAlertType?.("success");
        // setIsAlertOpen?.(true);
        setSelectedEmailTemplatesMap({});
      }
    } catch (error: any) {
      console.error("Error exporting templates:", error);
      setAlertMessage?.({
        open: true,
        message:
          error?.response?.data?.message || "Failed to export templates.",
        type: "error",
      });
    } finally {
      setLoading?.(false);
    }
  };

  const checkDisableWriteAction = () => {
    if (
      Object.values(selectedEmailTemplatesMap).flat().length === 0 ||
      !canWrite
    ) {
      return true;
    }
    return false;
  };

  const checkDisableDeleteAction = () => {
    if (
      Object.values(selectedEmailTemplatesMap).flat().length === 0 ||
      !canDelete
    ) {
      return true;
    }
    return false;
  };

  return (
    <div className="email-letter-mass-actions-wrapper email-letter-filters">
      <div className="email-letter-mass-actions-buttons-wrapper">
        <button
          className="import-btn"
          onClick={() => {
            handleExport();
          }}
          disabled={!canExport}
        >
          <div className="import-btn-text">
            {"Export"}
            <img
              src={importDownArrowIcon}
              alt="ImportDownArrow Icon"
              className="import-down-arrow-img"
            />
          </div>
        </button>
        <button
          className="import-btn"
          onClick={() => setIsMoveToFolderOpen(true)}
          disabled={checkDisableWriteAction()}
        >
          Move to Folder
        </button>
        <button
          className="import-btn delete-btn"
          onClick={() => setIsDeleteOpen(true)}
          disabled={checkDisableDeleteAction()}
        >
          <div className="import-btn-text">
            {"Archive"}
            <img
              src={redDeleteIcon}
              alt="ImportDownArrow Icon"
              className="import-down-arrow-img"
            />
          </div>
        </button>
      </div>
      {isMoveToFolderOpen && (
        <MoveTemplateToFolderPopup
          isOpen={isMoveToFolderOpen}
          onClose={() => {
            setSelectedEmailTemplatesMap({});
            setIsMoveToFolderOpen(false)
          }}
          onMove={(folder) => {
            handleMoveToFolder(folder);
          }}
          folders={folderOptions}
          templateType={templateType}
          fetchFoldersList={fetchFoldersList}
        />
      )}
      {isDeleteOpen && (
        <ConfirmationPopup
          open={isDeleteOpen}
          title={
            Object.values(selectedEmailTemplatesMap).flat().length === 1
              ? "Archive Template"
              : "Archive Templates"
          }
          buttonText="Archive"
          note={`Are you sure you want to archive the selected ${Object.values(selectedEmailTemplatesMap).flat().length === 1 ? "template" : "templates"}? This action cannot be undone.`}
          onCancel={() => setIsDeleteOpen(false)}
          onOkay={handleDelete}
        />
      )}
    </div>
  );
};

export default EmailLetterMassActions;
